import React, { useState, useCallback, useEffect, useContext, useRef } from "react";
import ReactDOM from "react-dom";
import { Document, Page, pdfjs } from "react-pdf";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { Localization, AppMessagesContext, SessionContext, actionSetErrorMessage, actionSetInfoMessage } from "@viamap/viamap2-common";
import { AdvancedDragModal, ADModalBody } from "src/componentsUtils/AdvancedDragModal";
import { ManagedWindow, closeWindow } from "src/WindowManager/WindowState";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { GlassButton } from "src/components/GlassButtons";
import { Spinner } from "react-bootstrap";
import { GlassInfo } from "src/components/MitGlassComponents";
import type { LLMAnalysisResult, LLMAnalysisResultItem } from "./ANNLLMAnalyzer";
import { cutPdfToSelectedPages } from "./ANNPDFCutter";
import { renderPdfPageToImage } from "./ANNPDFPageToImage";
import { uploadOverlayImage, deriveOverlayPlacement, blobToDataUrl } from "./ANNImageOverlayStorage";
import { ANNManager } from "./ANNManager";
import { AnnotationType, AnnotationContextType, ANNOTATION_TYPES, type AnnotationTypeCategory, AnnotationQuality } from "./ANNTypes";
import { MapitStateContext, actionAddDataLayer } from "src/states/MapitState";
import { LayerFunc } from "src/managers/LayerFunc";
import { ApplicationStateContext, Feature } from "src/states/ApplicationState";
import { useANNEditorContext, ANNEditorActionType } from "./ANNEditorState";
import "react-pdf/dist/Page/AnnotationLayer.css";
import "react-pdf/dist/Page/TextLayer.css";
import { MIBIcon } from "src/componentsUtils/MIBIcon";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

/** Vertical margin around the viewport (as % of root height) where PDF pages stay mounted. */
const PDF_PAGE_MOUNT_ROOT_MARGIN = "125% 0px 125% 0px";

/** Cap in-document search results to keep UI and memory predictable on noisy queries. */
const MAX_PDF_FIND_MATCHES = 500;

interface ANNPDFViewerProps {
   managedWindow: ManagedWindow;
}

interface WindowParams {
   pdfUrl: string;
   pdfBlob?: Blob;
   analysisResult?: LLMAnalysisResult;
   aktId?: string;
   documentId?: string;
   bfeNumber?: string | number;
   contextType?: AnnotationContextType;
   organizationId?: string;
   userId?: string;
   userName?: string;
   datoloebenr?: string;
   documentFilename?: string;
}

function SimpleButton({
   children,
   onClick,
   disabled,
   selected,
   variant,
   title,
   className
}: {
   children: React.ReactNode;
   onClick?: () => void;
   disabled?: boolean;
   selected?: boolean;
   variant?: 'default' | 'success' | 'primary' | 'muted';
   title?: string;
   className?: string;
}) {
   const variantClass =
      variant === "success" ? "ann-pdf-btn--success" :
         variant === "primary" ? "ann-pdf-btn--primary" :
            variant === "muted" ? "ann-pdf-btn--muted" : "";
   const selectedClass = selected ? "ann-pdf-btn--selected" : "";
   const classes = ["ann-pdf-btn", variantClass, selectedClass, className].filter(Boolean).join(" ");

   return (
      <button
         onClick={onClick}
         disabled={disabled}
         title={title}
         className={classes}
      >
         {children}
      </button>
   );
}

function PageButton({
   pageNum,
   isSelected,
   isAISuggested,
   isCurrent,
   onClick
}: {
   pageNum: number;
   isSelected: boolean;
   isAISuggested: boolean;
   isCurrent: boolean;
   onClick: () => void;
}) {
   const aiSuggestedLabel = (Localization.getText("LLM suggested") || "AI suggested").replace(/LLM/gi, "AI");
   const classes = [
      "ann-pdf-pageBtn",
      isSelected ? "ann-pdf-pageBtn--selected" : "",
      isCurrent ? "ann-pdf-pageBtn--current" : ""
   ].filter(Boolean).join(" ");
   return (
      <button
         onClick={onClick}
         className={classes}
         title={isAISuggested ? `${aiSuggestedLabel} - Page ${pageNum}` : `Page ${pageNum}`}
      >
         {pageNum}
      </button>
   );
}

function ControlButton({
   children,
   onClick,
   disabled,
   title
}: {
   children: React.ReactNode;
   onClick?: () => void;
   disabled?: boolean;
   title?: string;
}) {
   return (
      <button
         onClick={onClick}
         disabled={disabled}
         title={title}
         className="ann-pdf-controlBtn"
      >
         {children}
      </button>
   );
}

function SaveDocumentModal({
   open,
   onClose,
   onSave,
   saving,
   canVerify,
   defaultTitle,
   defaultHistoryComment
}: {
   open: boolean;
   onClose: () => void;
   onSave: (annotationType: AnnotationTypeCategory, quality: AnnotationQuality | undefined, verifyStamp: boolean, title: string, historyComment: string) => void;
   saving: boolean;
   canVerify: boolean;
   defaultTitle?: string;
   defaultHistoryComment?: string;
}) {
   const [annotationType, setAnnotationType] = useState<AnnotationTypeCategory>("Dokument");
   const [quality, setQuality] = useState<AnnotationQuality | undefined>(undefined);
   const [verifyStamp, setVerifyStamp] = useState(false);
   const [title, setTitle] = useState(defaultTitle || "");
   const [historyComment, setHistoryComment] = useState(defaultHistoryComment || "");

   useEffect(() => {
      if (defaultTitle) setTitle(defaultTitle);
      if (defaultHistoryComment) setHistoryComment(defaultHistoryComment);
   }, [defaultTitle, defaultHistoryComment]);

   if (!open) return null;

   return ReactDOM.createPortal(
      <AdvancedDragModal
         title={Localization.getText("Save Document Annotation")}
         allowFullscreen={false}
         allowUserMiniMize={false}
         PosDefault={{ left: "30%", top: "20%", width: "480px" }}
         onClose={onClose}
      >
         <ADModalBody>
            <div className="ann-create-form-container">
               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Annotation Type")}
                  </label>
                  <select
                     value={annotationType}
                     onChange={e => setAnnotationType(e.target.value as AnnotationTypeCategory)}
                     className="ann-create-select"
                  >
                     {ANNOTATION_TYPES.map(type => (
                        <option key={type} value={type}>{Localization.getText(type)}</option>
                     ))}
                  </select>
               </div>

               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Quality Assessment")}
                  </label>
                  <div className="ann-create-quality-selector-group">
                     <div
                        onClick={() => setQuality(undefined)}
                        className={`ann-create-quality-selector ${quality === undefined ? "ann-create-quality-selector-none ann-create-quality-selector-selected" : ""}`}
                     >
                        <span className="ann-create-quality-label">{Localization.getText("None")}</span>
                     </div>
                     {[AnnotationQuality.Gold, AnnotationQuality.Silver, AnnotationQuality.Bronze].map((q) => {
                        const isSelectedQ = quality === q;
                        const label = q === AnnotationQuality.Gold ? Localization.getText("Gold")
                           : q === AnnotationQuality.Silver ? Localization.getText("Silver")
                              : Localization.getText("Bronze");
                        return (
                           <div
                              key={q}
                              onClick={() => setQuality(q)}
                              className={`ann-create-quality-selector ${isSelectedQ ? "ann-create-quality-selector-selected" : ""} ann-quality-${q.toLowerCase()}`}
                           >
                              <div className={`ann-create-quality-indicator-circle ${isSelectedQ ? "ann-create-quality-indicator-circle-selected" : ""}`} />
                              <span className={`ann-create-quality-label ${isSelectedQ ? "ann-create-quality-label-selected" : ""}`}>{label}</span>
                           </div>
                        );
                     })}
                  </div>
               </div>

               {canVerify && (
                  <div>
                     <label
                        className={`ann-create-verifyCheckboxLabel ${verifyStamp ? "ann-create-verifyCheckboxLabel--checked" : ""}`}
                     >
                        <input
                           type="checkbox"
                           checked={verifyStamp}
                           onChange={(e) => setVerifyStamp(e.target.checked)}
                           className="ann-create-verifyCheckbox"
                        />
                        <MIBIcon icon="userVerified" />
                        <span className="ann-create-verifyText">
                           {Localization.getText("Stamp this annotation as verified")}
                        </span>
                     </label>
                  </div>
               )}

               <div>
                  <label className="ann-create-label">
                     {Localization.getText("Title")}
                  </label>
                  <input
                     value={title}
                     onChange={(e) => setTitle(e.target.value)}
                     className="ann-create-input"
                  />
               </div>

               <div>
                  <label className="ann-create-label">
                     {Localization.getText("History comment")}
                  </label>
                  <textarea
                     rows={3}
                     value={historyComment}
                     onChange={(e) => setHistoryComment(e.target.value)}
                     className="ann-create-textarea"
                     placeholder={Localization.getText("Optional history comment")}
                  />
               </div>

               <div className="ann-create-button-group">
                  <GlassButton onClick={onClose}>{Localization.getText("Cancel")}</GlassButton>
                  <GlassButton disabled={saving || !title.trim()} onClick={() => onSave(annotationType, quality, verifyStamp, title, historyComment)}>
                     {saving ? Localization.getText("Saving...") : Localization.getText("Save")}
                  </GlassButton>
               </div>
            </div>
         </ADModalBody>
      </AdvancedDragModal>,
      document.body
   );
}

export function ANNPDFViewer({ managedWindow }: ANNPDFViewerProps) {
   const { dispatch: windowDispatch } = useWindowContext();
   const { dispatch: appMessageDispatch } = useContext(AppMessagesContext);
   const { state: sessionState } = useContext(SessionContext);
   const { dispatch: mapitStateDispatch, state: mapitState } = useContext(MapitStateContext);
   const { hasAccessToFeature } = useContext(ApplicationStateContext);
   const { dispatch: editorDispatch } = useANNEditorContext();
   const canVerify = hasAccessToFeature(Feature.AnnotationAuthUser);
   const canWriteAnnotation = hasAccessToFeature(Feature.AnnotationWrite);
   const params = managedWindow.windowParams as WindowParams | undefined;

   const [numPages, setNumPages] = useState<number>(0);
   const [currentPage, setCurrentPage] = useState<number>(1);
   const [scale, setScale] = useState<number>(1.0);
   const [loading, setLoading] = useState<boolean>(true);
   const [error, setError] = useState<string | null>(null);
   const [pdfData, setPdfData] = useState<string | ArrayBuffer | null>(null);

   const [selectedPages, setSelectedPages] = useState<Set<number>>(new Set());
   const [saving, setSaving] = useState<boolean>(false);
   const [uploadingPage, setUploadingPage] = useState<number | null>(null);
   const [showSaveModal, setShowSaveModal] = useState<boolean>(false);
   const displayRef = useRef<HTMLDivElement | null>(null);
   const didInitialAiScrollRef = useRef(false);
   const pdfDocRef = useRef<PDFDocumentProxy | null>(null);
   const scaleRef = useRef(scale);
   const pageVisibilityRef = useRef(new Map<number, number>());
   /** While true, mount observer only adds pages — avoids unmounting jump targets before scrollIntoView finishes. */
   const scrollActivityRef = useRef(false);
   const scrollIdleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

   const bumpScrollActivity = useCallback(() => {
      scrollActivityRef.current = true;
      if (scrollIdleTimerRef.current != null) {
         clearTimeout(scrollIdleTimerRef.current);
      }
      scrollIdleTimerRef.current = setTimeout(() => {
         scrollActivityRef.current = false;
         scrollIdleTimerRef.current = null;
      }, 450);
   }, []);

   /** Estimated rendered page height at current scale (from page 1); used for off-screen placeholders. */
   const [estimatedPageHeight, setEstimatedPageHeight] = useState<number | null>(null);
   /** Page numbers with an active <Page /> (near viewport). */
   const [mountedPdfPages, setMountedPdfPages] = useState<Set<number>>(() => new Set());

   const findInputRef = useRef<HTMLInputElement | null>(null);
   const findGenerationRef = useRef(0);
   const findLastBuiltQueryRef = useRef("");
   const findMatchesRef = useRef<{ page: number }[]>([]);
   const [findQuery, setFindQuery] = useState("");
   const [findMatches, setFindMatches] = useState<{ page: number }[]>([]);
   const [findMatchIndex, setFindMatchIndex] = useState(-1);
   const [findSearchStatus, setFindSearchStatus] = useState<"idle" | "searching" | "done">("idle");
   const [findCapped, setFindCapped] = useState(false);

   findMatchesRef.current = findMatches;

   const analysisItem: LLMAnalysisResultItem | undefined = params?.analysisResult?.items?.[0];
   const pagesKept = analysisItem?.pages_kept || [];
   const pagesKeptKey = pagesKept.join(",");

   useEffect(() => {
      if (pagesKept.length > 0) {
         setSelectedPages(new Set(pagesKept));
      }
   }, [pagesKeptKey]);

   useEffect(() => {
      if (params?.pdfBlob) {
         const reader = new FileReader();
         reader.onload = () => {
            setPdfData(reader.result);
         };
         reader.onerror = () => {
            setError("Failed to read PDF data");
         };
         reader.readAsArrayBuffer(params.pdfBlob);
      } else if (params?.pdfUrl) {
         setPdfData(params.pdfUrl);
      }
   }, [params?.pdfBlob, params?.pdfUrl]);

   useEffect(() => {
      didInitialAiScrollRef.current = false;
   }, [pdfData]);

   useEffect(() => {
      scaleRef.current = scale;
   }, [scale]);

   useEffect(() => {
      findGenerationRef.current += 1;
      findLastBuiltQueryRef.current = "";
      setFindQuery("");
      setFindMatches([]);
      setFindMatchIndex(-1);
      setFindSearchStatus("idle");
      setFindCapped(false);
      setMountedPdfPages(new Set());
      setEstimatedPageHeight(null);
      pdfDocRef.current = null;
      pageVisibilityRef.current.clear();
   }, [pdfData]);

   const onDocumentLoadSuccess = useCallback(async (pdf: PDFDocumentProxy) => {
      pdfDocRef.current = pdf;
      setNumPages(pdf.numPages);
      setLoading(false);

      const anchor = pagesKept.length > 0 ? pagesKept[0] : 1;
      const seed = new Set<number>();
      for (let i = Math.max(1, anchor - 2); i <= Math.min(pdf.numPages, anchor + 2); i++) {
         seed.add(i);
      }
      seed.add(1);
      setMountedPdfPages(seed);

      if (pagesKept.length > 0) {
         setCurrentPage(pagesKept[0]);
      }

      try {
         const p1 = await pdf.getPage(1);
         const v = p1.getViewport({ scale: scaleRef.current });
         setEstimatedPageHeight(v.height);
      } catch {
         setEstimatedPageHeight(800);
      }
   }, [pagesKeptKey]);

   useEffect(() => {
      const pdf = pdfDocRef.current;
      if (!pdf || numPages === 0) {
         return;
      }
      let cancelled = false;
      pdf
         .getPage(1)
         .then((p) => {
            if (cancelled) {
               return;
            }
            const v = p.getViewport({ scale });
            setEstimatedPageHeight(v.height);
         })
         .catch(() => {
            if (!cancelled) {
               setEstimatedPageHeight(800);
            }
         });
      return () => {
         cancelled = true;
      };
   }, [scale, numPages]);

   const onDocumentLoadError = useCallback((error: Error) => {
      setError(`Failed to load PDF: ${error.message}`);
      setLoading(false);
   }, []);

   const ensurePagesAround = useCallback((center: number) => {
      bumpScrollActivity();
      setMountedPdfPages((prev) => {
         const next = new Set(prev);
         const lo = Math.max(1, center - 2);
         const hi = Math.min(numPages, center + 2);
         for (let p = lo; p <= hi; p++) {
            next.add(p);
         }
         return next;
      });
   }, [numPages, bumpScrollActivity]);

   const scrollToPdfPage = useCallback((pageNum: number, behavior: ScrollBehavior = "smooth") => {
      const container = displayRef.current;
      if (!container || pageNum < 1) {
         return;
      }
      ensurePagesAround(pageNum);
      setCurrentPage(pageNum);
      requestAnimationFrame(() => {
         const el = container.querySelector<HTMLElement>(`[data-ann-pdf-page="${pageNum}"]`);
         el?.scrollIntoView({ behavior, block: "start" });
      });
   }, [ensurePagesAround]);

   const runPdfSearch = useCallback(
      async (qRaw: string) => {
         const pdf = pdfDocRef.current;
         const q = qRaw.trim().toLowerCase();
         if (!pdf || !q) {
            setFindMatches([]);
            setFindMatchIndex(-1);
            setFindSearchStatus("idle");
            setFindCapped(false);
            return;
         }
         const gen = ++findGenerationRef.current;
         setFindSearchStatus("searching");
         setFindCapped(false);
         const matches: { page: number }[] = [];
         let capped = false;
         const n = pdf.numPages;
         try {
            for (let p = 1; p <= n; p++) {
               if (gen !== findGenerationRef.current) {
                  return;
               }
               const page = await pdf.getPage(p);
               const tc = await page.getTextContent();
               const text = tc.items.map((it) => ("str" in it ? it.str : "")).join("");
               const lower = text.toLowerCase();
               let pos = 0;
               while (matches.length < MAX_PDF_FIND_MATCHES) {
                  const idx = lower.indexOf(q, pos);
                  if (idx === -1) {
                     break;
                  }
                  matches.push({ page: p });
                  pos = idx + 1;
               }
               if (matches.length >= MAX_PDF_FIND_MATCHES) {
                  capped = true;
                  break;
               }
               await new Promise((r) => setTimeout(r, 0));
            }
         } catch {
            if (gen !== findGenerationRef.current) {
               return;
            }
            setFindMatches([]);
            setFindMatchIndex(-1);
            setFindSearchStatus("done");
            setFindCapped(false);
            return;
         }
         if (gen !== findGenerationRef.current) {
            return;
         }
         setFindMatches(matches);
         setFindMatchIndex(matches.length > 0 ? 0 : -1);
         setFindCapped(capped);
         setFindSearchStatus("done");
         if (matches.length > 0) {
            scrollToPdfPage(matches[0].page, "auto");
         }
      },
      [scrollToPdfPage]
   );

   const goFindNext = useCallback(() => {
      const matches = findMatchesRef.current;
      if (matches.length === 0) {
         return;
      }
      setFindMatchIndex((prev) => {
         const next = prev < 0 ? 0 : (prev + 1) % matches.length;
         const page = matches[next].page;
         requestAnimationFrame(() => scrollToPdfPage(page, "auto"));
         return next;
      });
   }, [scrollToPdfPage]);

   const goFindPrev = useCallback(() => {
      const matches = findMatchesRef.current;
      if (matches.length === 0) {
         return;
      }
      setFindMatchIndex((prev) => {
         const next = prev <= 0 ? matches.length - 1 : prev - 1;
         const page = matches[next].page;
         requestAnimationFrame(() => scrollToPdfPage(page, "auto"));
         return next;
      });
   }, [scrollToPdfPage]);

   const onFindSubmit = useCallback(() => {
      const q = findQuery.trim();
      if (!q) {
         findGenerationRef.current += 1;
         findLastBuiltQueryRef.current = "";
         setFindMatches([]);
         setFindMatchIndex(-1);
         setFindSearchStatus("idle");
         setFindCapped(false);
         return;
      }
      if (findLastBuiltQueryRef.current === q && findMatchesRef.current.length > 0) {
         goFindNext();
         return;
      }
      findLastBuiltQueryRef.current = q;
      void runPdfSearch(q);
   }, [findQuery, goFindNext, runPdfSearch]);

   useEffect(() => {
      const onKey = (e: KeyboardEvent) => {
         if ((e.ctrlKey || e.metaKey) && e.key === "f") {
            e.preventDefault();
            findInputRef.current?.focus();
            findInputRef.current?.select();
         }
      };
      window.addEventListener("keydown", onKey);
      return () => window.removeEventListener("keydown", onKey);
   }, []);

   const goToPrevPage = useCallback(() => {
      setCurrentPage((prev) => {
         const next = Math.max(1, prev - 1);
         ensurePagesAround(next);
         requestAnimationFrame(() => {
            const container = displayRef.current;
            const el = container?.querySelector<HTMLElement>(`[data-ann-pdf-page="${next}"]`);
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
         });
         return next;
      });
   }, [ensurePagesAround]);

   const goToNextPage = useCallback(() => {
      setCurrentPage((prev) => {
         const next = Math.min(numPages, prev + 1);
         ensurePagesAround(next);
         requestAnimationFrame(() => {
            const container = displayRef.current;
            const el = container?.querySelector<HTMLElement>(`[data-ann-pdf-page="${next}"]`);
            el?.scrollIntoView({ behavior: "smooth", block: "start" });
         });
         return next;
      });
   }, [numPages, ensurePagesAround]);

   const zoomIn = useCallback(() => {
      setScale(prev => Math.min(3, prev + 0.25));
   }, []);

   const zoomOut = useCallback(() => {
      setScale(prev => Math.max(0.5, prev - 0.25));
   }, []);

   const isAISuggestedPage = (page: number): boolean => {
      return pagesKept.includes(page);
   };

   const isSelectedPage = (page: number): boolean => {
      return selectedPages.has(page);
   };

   const togglePageSelection = useCallback((page: number) => {
      setSelectedPages(prev => {
         const newSet = new Set(prev);
         if (newSet.has(page)) {
            newSet.delete(page);
         } else {
            newSet.add(page);
         }
         return newSet;
      });
   }, []);

   const selectAllPages = useCallback(() => {
      const allPages = new Set<number>();
      for (let i = 1; i <= numPages; i++) {
         allPages.add(i);
      }
      setSelectedPages(allPages);
   }, [numPages]);

   const deselectAllPages = useCallback(() => {
      setSelectedPages(new Set());
   }, []);

   const resetToAISuggestions = useCallback(() => {
      setSelectedPages(new Set(pagesKept));
   }, [pagesKeptKey]);

   const handleSaveDocument = useCallback(async (
      annotationType: AnnotationTypeCategory,
      quality: AnnotationQuality | undefined,
      verifyStamp: boolean,
      title: string,
      historyComment: string
   ) => {
      if (!canWriteAnnotation) {
         return;
      }

      if (!params?.pdfBlob) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No PDF data available")));
         return;
      }

      if (selectedPages.size === 0) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No pages selected")));
         return;
      }

      const organizationId = params.organizationId || sessionState?.customerRef;
      const userId = params.userId || sessionState?.userRef;
      const userName = params.userName || sessionState?.userName;

      if (!organizationId || !userId || !userName) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No organization selected")));
         return;
      }

      const hasSaveContext = params.contextType === AnnotationContextType.Ejendom
         ? Boolean(params.bfeNumber || params.documentId)
         : Boolean(params.documentId);

      if (!params.contextType || !hasSaveContext) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Missing context for saving annotation")));
         return;
      }
      if (!title.trim()) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("Title is required")));
         return;
      }

      setSaving(true);
      try {
         const pagesToKeep = Array.from(selectedPages).sort((a, b) => a - b);
         const cutPdfBlob = await cutPdfToSelectedPages(params.pdfBlob, pagesToKeep);
         const filename = `${params.datoloebenr || params.aktId || 'document'}_cut_${pagesToKeep.length}pages.pdf`;
         const file = new File([cutPdfBlob], filename, { type: 'application/pdf' });
         const uploadResult = await uploadOverlayImage(file);

         await ANNManager.saveAnnotation({
            type: AnnotationType.Document,
            documentId: params.contextType === AnnotationContextType.Servitut ? params.documentId : undefined,
            bfeNumber: params.contextType === AnnotationContextType.Ejendom
               ? (params.bfeNumber ?? params.documentId)
               : undefined,
            contextType: params.contextType,
            organizationId,
            userId,
            userName,
            datoloebenr: params.datoloebenr,
            document: {
               filename,
               s3Key: uploadResult.key,
               s3Url: uploadResult.url,
               mimeType: 'application/pdf',
               size: cutPdfBlob.size,
            },
            title: title.trim(),
            historyComment: historyComment.trim() || `Extracted pages: ${pagesToKeep.join(', ')}`,
            annotationType,
            quality,
            verified: canVerify && verifyStamp,
         });

         appMessageDispatch(actionSetInfoMessage(
            Localization.getText("Document saved successfully")
         ));

         setShowSaveModal(false);
         editorDispatch({ type: ANNEditorActionType.SaveSuccess });
         await new Promise(resolve => setTimeout(resolve, 1000));
         windowDispatch(closeWindow(managedWindow.id));
      } catch (err: any) {
         appMessageDispatch(actionSetErrorMessage(
            err?.message || Localization.getText("Failed to save document")
         ));
      } finally {
         setSaving(false);
      }
   }, [params, selectedPages, sessionState, appMessageDispatch, canWriteAnnotation, canVerify, editorDispatch, windowDispatch, managedWindow.id]);

   const handleUploadToMap = useCallback(async (pageNumber: number) => {
      if (!params?.pdfBlob) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No PDF data available")));
         return;
      }

      setUploadingPage(pageNumber);
      try {
         const imageBlob = await renderPdfPageToImage(params.pdfBlob, pageNumber, 2.0);
         const filename = `${params.aktId || 'page'}_page${pageNumber}.png`;
         const file = new File([imageBlob], filename, { type: 'image/png' });
         await uploadOverlayImage(file);
         const dataUrl = await blobToDataUrl(imageBlob);
         const { coordinates } = await deriveOverlayPlacement({ blob: imageBlob }, mapitState.map?.getMapPop?.());
         const layerName = `${params.aktId || 'PDF'} - Page ${pageNumber}`;
         const newLayer = LayerFunc.createLayerInfoForImageOverlay(
            layerName,
            filename,
            dataUrl,
            coordinates
         );

         mapitStateDispatch(actionAddDataLayer(newLayer));

         appMessageDispatch(actionSetInfoMessage(
            Localization.getText("Page uploaded to map")
         ));
      } catch (err: any) {
         appMessageDispatch(actionSetErrorMessage(
            err?.message || Localization.getText("Failed to upload page to map")
         ));
      } finally {
         setUploadingPage(null);
      }
   }, [params, mapitState.map, mapitStateDispatch, appMessageDispatch]);

   const handleDownloadCutPdf = useCallback(async () => {
      if (!params?.pdfBlob) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No PDF data available")));
         return;
      }

      if (selectedPages.size === 0) {
         appMessageDispatch(actionSetErrorMessage(Localization.getText("No pages selected")));
         return;
      }

      setSaving(true);
      try {
         const pagesToKeep = Array.from(selectedPages).sort((a, b) => a - b);
         const cutPdfBlob = await cutPdfToSelectedPages(params.pdfBlob, pagesToKeep);
         const filename = `${params.datoloebenr || params.aktId || 'document'}_pages_${pagesToKeep.join('-')}.pdf`;
         const url = URL.createObjectURL(cutPdfBlob);
         const link = document.createElement('a');
         link.href = url;
         link.download = filename;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         URL.revokeObjectURL(url);
      } catch (err: any) {
         appMessageDispatch(actionSetErrorMessage(
            err?.message || Localization.getText("Failed to download PDF")
         ));
      } finally {
         setSaving(false);
      }
   }, [params, selectedPages, appMessageDispatch]);

   const handleClose = useCallback(() => {
      windowDispatch(closeWindow(managedWindow.id));
   }, [windowDispatch, managedWindow.id]);

   useEffect(() => {
      const root = displayRef.current;
      if (!root || numPages === 0) {
         return;
      }

      root.addEventListener("scroll", bumpScrollActivity, { passive: true });

      const parsePage = (el: Element): number | null => {
         const raw = (el as HTMLElement).dataset.annPdfPage;
         if (raw === undefined) {
            return null;
         }
         const p = Number(raw);
         return Number.isFinite(p) ? p : null;
      };

      const ioMount = new IntersectionObserver(
         (entries) => {
            setMountedPdfPages((prev) => {
               const next = new Set(prev);
               for (const e of entries) {
                  const p = parsePage(e.target);
                  if (p === null) {
                     continue;
                  }
                  if (e.isIntersecting) {
                     next.add(p);
                  } else if (!scrollActivityRef.current) {
                     next.delete(p);
                  }
               }
               return next;
            });
         },
         { root, rootMargin: PDF_PAGE_MOUNT_ROOT_MARGIN, threshold: 0 }
      );

      const ioCurrent = new IntersectionObserver(
         (entries) => {
            for (const e of entries) {
               const p = parsePage(e.target);
               if (p === null) {
                  continue;
               }
               if (e.isIntersecting && e.intersectionRect.height > 0) {
                  pageVisibilityRef.current.set(
                     p,
                     e.intersectionRect.width * e.intersectionRect.height
                  );
               } else {
                  pageVisibilityRef.current.delete(p);
               }
            }
            let best = 0;
            let bestPage = 1;
            pageVisibilityRef.current.forEach((area, pp) => {
               if (area > best) {
                  best = area;
                  bestPage = pp;
               }
            });
            if (best > 0) {
               setCurrentPage((prev) => (prev !== bestPage ? bestPage : prev));
            }
         },
         { root, rootMargin: "0px", threshold: [0, 0.1, 0.25, 0.5, 0.75, 1] }
      );

      root.querySelectorAll<HTMLElement>("[data-ann-pdf-page]").forEach((el) => {
         ioMount.observe(el);
         ioCurrent.observe(el);
      });

      return () => {
         root.removeEventListener("scroll", bumpScrollActivity);
         if (scrollIdleTimerRef.current != null) {
            clearTimeout(scrollIdleTimerRef.current);
            scrollIdleTimerRef.current = null;
         }
         ioMount.disconnect();
         ioCurrent.disconnect();
      };
   }, [numPages, pdfData, bumpScrollActivity]);

   /** After placeholders exist, scroll to first AI-suggested page once. */
   useEffect(() => {
      if (loading || numPages === 0 || didInitialAiScrollRef.current) return;
      if (pagesKept.length === 0) {
         didInitialAiScrollRef.current = true;
         return;
      }
      const first = pagesKept[0];
      didInitialAiScrollRef.current = true;
      requestAnimationFrame(() => {
         requestAnimationFrame(() => {
            scrollToPdfPage(first, "auto");
         });
      });
   }, [loading, numPages, pagesKeptKey, scrollToPdfPage]);

   if (!params || (!params.pdfUrl && !params.pdfBlob)) {
      return (
         <AdvancedDragModal
            window={managedWindow}
            title={Localization.getText("PDF Analyzer")}
            onClose={handleClose}
            allowFullscreen={true}
            allowUserMiniMize={true}
            PosDefault={{ width: "1120px", height: "800px", left: "5%", top: "5%" }}
         >
            <ADModalBody>
               <GlassInfo>
                  <div className="ann-pdf-noData">
                     {Localization.getText("No PDF data available")}
                  </div>
               </GlassInfo>
            </ADModalBody>
         </AdvancedDragModal>
      );
   }

   const canSave = Boolean(
      canWriteAnnotation &&
      params.contextType &&
      (params.contextType === AnnotationContextType.Ejendom
         ? (params.bfeNumber || params.documentId)
         : params.documentId) &&
      selectedPages.size > 0
   );
   const confidenceDisplay = analysisItem?.confidence
      ? (analysisItem.confidence > 1 ? analysisItem.confidence : Math.round(analysisItem.confidence * 10))
      : null;

   const defaultHistoryComment = analysisItem?.summary
      ? `${analysisItem.summary.substring(0, 200)}${analysisItem.summary.length > 200 ? '...' : ''}`
      : '';
   const defaultTitle = params?.documentFilename || params?.datoloebenr || params?.aktId || Localization.getText("Document");
   const aiSuggestedLabel = (Localization.getText("LLM suggested") || "AI suggested").replace(/LLM/gi, "AI");

   const titleParts: string[] = [Localization.getText("PDF Analyzer")];
   if (params.documentFilename) {
      titleParts.push(params.documentFilename);
   }
   if (params.datoloebenr) {
      titleParts.push(params.datoloebenr);
   } else if (params.aktId) {
      titleParts.push(params.aktId);
   }
   const windowTitle = titleParts.join(" - ");

   return (
      <AdvancedDragModal
         window={managedWindow}
         title={windowTitle}
         onClose={handleClose}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ width: "1120px", height: "800px", left: "5%", top: "5%" }}
      >
         <ADModalBody>
            <div className="ann-pdf-viewer">
               <div className="ann-pdf-columns">
                  <div className="ann-pdf-leftColumn">
                     <div className="ann-pdf-panel">
                        <div className="ann-pdf-pageSelectionHeader">
                           <strong>{Localization.getText("Page Selection")}:</strong>
                           <SimpleButton onClick={selectAllPages}>{Localization.getText("All")}</SimpleButton>
                           <SimpleButton onClick={deselectAllPages}>{Localization.getText("None")}</SimpleButton>
                           {pagesKept.length > 0 && (
                              <SimpleButton onClick={resetToAISuggestions}>{Localization.getText("AI")}</SimpleButton>
                           )}
                           <span className="ann-pdf-pageSelectionCount">
                              {selectedPages.size} / {numPages} {Localization.getText("pages")}
                           </span>
                        </div>
                        <div className="ann-pdf-pageSelectionButtons">
                           {Array.from({ length: numPages }, (_, i) => i + 1).map(pageNum => (
                              <PageButton
                                 key={pageNum}
                                 pageNum={pageNum}
                                 isSelected={isSelectedPage(pageNum)}
                                 isAISuggested={isAISuggestedPage(pageNum)}
                                 isCurrent={currentPage === pageNum}
                                 onClick={() => scrollToPdfPage(pageNum)}
                              />
                           ))}
                        </div>
                        <div className="ann-pdf-pageSelectionActions">
                           <SimpleButton
                              onClick={handleDownloadCutPdf}
                              disabled={saving || selectedPages.size === 0}
                              variant="default"
                           >
                              <MIBIcon icon="download" />
                              {Localization.getText("Download Selected")}
                           </SimpleButton>

                           {canWriteAnnotation ? (
                              <SimpleButton
                                 onClick={() => setShowSaveModal(true)}
                                 disabled={saving || !canSave}
                                 variant="default"
                              >
                                 <MIBIcon icon="checkmark" />
                                 {Localization.getText("Save as Annotation")}
                              </SimpleButton>
                           ) : null}
                        </div>
                     </div>

                     {analysisItem && (
                        <div className="ann-pdf-panel">
                           <div className="ann-pdf-analysisLeft">
                              <strong>{Localization.getText("Summary")}:</strong>
                              <p className="ann-pdf-analysisSummaryText">{analysisItem.summary || "-"}</p>
                           </div>
                           <div className="ann-pdf-analysisMeta">
                              <div><strong>{Localization.getText("Confidence")}:</strong> {confidenceDisplay !== null ? `${confidenceDisplay}/10` : "-"}</div>
                              <div><strong>{aiSuggestedLabel}:</strong> {pagesKept.length} {Localization.getText("pages")}</div>
                           </div>
                           {analysisItem.reasoning && (
                              <details className="ann-pdf-analysisDetails">
                                 <summary><strong>{Localization.getText("Reasoning")}</strong></summary>
                                 <p>{analysisItem.reasoning}</p>
                              </details>
                           )}
                        </div>
                     )}
                  </div>

                  <div className="ann-pdf-rightColumn">
                     <div className="ann-pdf-toolbar">
                        <div className="ann-pdf-toolbarGroup ann-pdf-toolbarFindRow">
                           <input
                              ref={findInputRef}
                              type="search"
                              className="ann-pdf-findInput"
                              value={findQuery}
                              onChange={(e) => setFindQuery(e.target.value)}
                              onKeyDown={(e) => {
                                 if (e.key === "Enter") {
                                    e.preventDefault();
                                    onFindSubmit();
                                 }
                              }}
                              placeholder={Localization.getText("Find in PDF")}
                              disabled={loading || numPages === 0}
                              aria-label={Localization.getText("Find in PDF")}
                           />
                           <SimpleButton
                              onClick={onFindSubmit}
                              disabled={loading || numPages === 0 || findSearchStatus === "searching"}
                              variant="muted"
                              title={Localization.getText("Find")}
                           >
                              {Localization.getText("Find")}
                           </SimpleButton>
                           <ControlButton
                              onClick={goFindPrev}
                              disabled={findMatches.length === 0 || findSearchStatus === "searching"}
                              title={Localization.getText("Find previous match")}
                           >
                              <MIBIcon icon="goLeft" />
                           </ControlButton>
                           <ControlButton
                              onClick={goFindNext}
                              disabled={findMatches.length === 0 || findSearchStatus === "searching"}
                              title={Localization.getText("Find next match")}
                           >
                              <MIBIcon icon="goRight" />
                           </ControlButton>
                           {findSearchStatus === "searching" ? (
                              <Spinner animation="border" size="sm" className="ann-pdf-miniSpinner" />
                           ) : null}
                           <span className="ann-pdf-findMeta">
                              {findMatches.length > 0
                                 ? `${findMatchIndex + 1} / ${findMatches.length}`
                                 : findSearchStatus === "done" && findQuery.trim() && findMatches.length === 0
                                    ? Localization.getText("No matches in PDF")
                                    : null}
                              {findCapped ? ` · ${Localization.getText("PDF find limit reached")}` : ""}
                           </span>
                        </div>

                        <div className="ann-pdf-toolbarGroup">
                           <ControlButton
                              onClick={zoomOut}
                              title={Localization.getText("Zoom out")}
                           >
                              <MIBIcon icon="zoomOut" />
                           </ControlButton>
                           <span className="ann-pdf-toolbarValue">{Math.round(scale * 100)}%</span>
                           <ControlButton
                              onClick={zoomIn}
                              title={Localization.getText("Zoom in")}
                           >
                              <MIBIcon icon="zoomIn" />
                           </ControlButton>
                        </div>

                        <div className="ann-pdf-divider" />

                        <div className="ann-pdf-toolbarGroup">
                           <ControlButton
                              onClick={goToPrevPage}
                              disabled={currentPage <= 1}
                              title={Localization.getText("Previous page")}
                           >
                              <MIBIcon icon="goLeft" />
                           </ControlButton>
                           <span className="ann-pdf-toolbarPageValue">
                              {currentPage} / {numPages}
                           </span>
                           <ControlButton
                              onClick={goToNextPage}
                              disabled={currentPage >= numPages}
                              title={Localization.getText("Next page")}
                           >
                              <MIBIcon icon="goRight" />
                           </ControlButton>
                        </div>

                        <div className="ann-pdf-divider ann-pdf-divider--wide" />

                        <SimpleButton
                           onClick={() => togglePageSelection(currentPage)}
                           variant={isSelectedPage(currentPage) ? 'success' : 'default'}
                        >
                           <MIBIcon icon="checkmark" />
                           {isSelectedPage(currentPage)
                              ? Localization.getText("Selected")
                              : Localization.getText("Select page")}
                        </SimpleButton>

                        <SimpleButton
                           onClick={() => handleUploadToMap(currentPage)}
                           disabled={uploadingPage === currentPage}
                        >
                           {uploadingPage === currentPage
                              ? <Spinner animation="border" size="sm" className="ann-pdf-miniSpinner" />
                              : <MIBIcon icon="addToMap" />}
                           {Localization.getText("Upload to Map")}
                        </SimpleButton>
                     </div>

                     <div className="ann-pdf-display" ref={displayRef}>
                        {loading && (
                           <div className="ann-pdf-loading">
                              <Spinner animation="border" role="status" />
                              <div className="ann-pdf-loadingText">{Localization.getText("Loading")}...</div>
                           </div>
                        )}

                        {error && (
                           <div className="ann-pdf-displayError">
                              {error}
                           </div>
                        )}

                        {pdfData && (
                           <Document
                              file={pdfData}
                              onLoadSuccess={onDocumentLoadSuccess}
                              onLoadError={onDocumentLoadError}
                              loading={null}
                           >
                              <div className="ann-pdf-pagesStack">
                                 {numPages > 0 &&
                                    Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => {
                                       const suggested = isAISuggestedPage(pageNum);
                                       const selected = isSelectedPage(pageNum);
                                       const frameClass =
                                          suggested && selected
                                             ? "ann-pdf-pageFrame--suggestedSelected"
                                             : selected
                                                ? "ann-pdf-pageFrame--selected"
                                                : suggested
                                                   ? "ann-pdf-pageFrame--suggested"
                                                   : "ann-pdf-pageFrame--none";
                                       const mountPage = mountedPdfPages.has(pageNum);
                                       const placeholderMinHeight = (estimatedPageHeight ?? 480) + 8;
                                       return (
                                          <div
                                             key={pageNum}
                                             className={`ann-pdf-pageFrame ${frameClass}`}
                                             data-ann-pdf-page={pageNum}
                                             style={!mountPage ? { minHeight: placeholderMinHeight } : undefined}
                                          >
                                             {suggested && (
                                                <div
                                                   className={`ann-pdf-suggestedBadge ${selected ? "ann-pdf-suggestedBadge--selected" : ""}`}
                                                >
                                                   {aiSuggestedLabel}
                                                </div>
                                             )}
                                             {mountPage ? (
                                                <Page
                                                   pageNumber={pageNum}
                                                   scale={scale}
                                                   renderTextLayer={true}
                                                   renderAnnotationLayer={true}
                                                />
                                             ) : (
                                                <div
                                                   className="ann-pdf-pagePlaceholder"
                                                   style={{ minHeight: placeholderMinHeight - 8 }}
                                                />
                                             )}
                                          </div>
                                       );
                                    })}
                              </div>
                           </Document>
                        )}
                     </div>
                  </div>
               </div>
            </div>

            {canWriteAnnotation ? (
               <SaveDocumentModal
                  open={showSaveModal}
                  onClose={() => setShowSaveModal(false)}
                  onSave={handleSaveDocument}
                  saving={saving}
                  canVerify={canVerify}
                  defaultTitle={defaultTitle}
                  defaultHistoryComment={defaultHistoryComment}
               />
            ) : null}
         </ADModalBody>
      </AdvancedDragModal>
   );
}
